import React, { useEffect, useRef } from "react";
import StagingBanner from "./StagingBanner";
import DemoBanner from "./DemoBanner";

/**
 * Wraps the environment banners (Staging + Demo) in a single sticky
 * strip at the top of the page.
 *
 * Why this exists: the sidebar is fixed-positioned and used to cover
 * the bottom edge of the demo banner, hiding the time-remaining chip.
 * Sticking + raising the banners' z-index alone wasn't enough — the
 * sidebar's content would still hide behind them. We measure the
 * combined banner height with a ResizeObserver and write it to a CSS
 * variable on <html>, and the sidebar uses that variable to offset
 * its `top` so it slides in below the banners cleanly.
 *
 * When neither banner is rendering anything, the wrapper height is 0
 * and the sidebar sits flush at top:0 — no penalty for prod sessions.
 */
export default function TopBanners() {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const writeHeight = () => {
      const h = el.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty("--top-banner-h", `${Math.round(h)}px`);
    };

    writeHeight();

    // ResizeObserver fires when the banners mount/unmount (Demo renders
    // null for non-demo users), when the user dismisses a banner, and
    // when the viewport changes width and the text wraps to two lines.
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(writeHeight);
      ro.observe(el);
    } else {
      window.addEventListener("resize", writeHeight);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", writeHeight);
      // Reset on unmount so a stale value doesn't leak into a route
      // where banners don't apply (logged-out pages mount their own
      // markup but inherit the same <html> root).
      document.documentElement.style.removeProperty("--top-banner-h");
    };
  }, []);

  return (
    <div ref={ref} className="sticky top-0 z-[60] w-full">
      <StagingBanner />
      <DemoBanner />
    </div>
  );
}
